import { safeJsonObject } from '../lib/safeJson'
import type { ColumnInfo, ItemInfo } from '../api/client'
import { batteryValue } from './battery'
import { evaluateFormula, formulaReferences, parseFormula, type ParsedFormula } from './formula'

/**
 * M9.6：把「派生列」（进度列 + 公式列）一次性算好，供表格渲染与组尾统计使用。
 *
 * 为什么要前端算：服务端只在 GET 看板时算，改单元格后不刷新就不会变。
 * 这里的规则必须与服务端 `DerivedColumns.cpp` 保持一致。
 */

interface FormulaCol
{
  col: ColumnInfo
  parsed: ParsedFormula
  deps: string[]	// 依赖的公式列 key
}

function readFormulaSetting(col: ColumnInfo): string {
  const raw = col.settings as unknown
  if (raw === null || raw === undefined) return ''
  if (typeof raw === 'object') {
    const f = (raw as { formula?: unknown }).formula
    return typeof f === 'string' ? f : ''
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = safeJsonObject<{ formula?: unknown }>(raw, {}, 'formula')
    return typeof parsed.formula === 'string' ? parsed.formula : ''
  }
  return ''
}

/**
 * 返回 itemId → 该条目所有派生列的值。
 * 只包含能算出来的列；调用方按 key 覆盖即可。
 */
export function computeDerivedValues(
  columns: ColumnInfo[],
  items: ItemInfo[],
): Map<number, Record<string, string>> {
  const out = new Map<number, Record<string, string>>()
  const ensure = (id: number): Record<string, string> => {
    const cur = out.get(id)
    if (cur !== undefined) return cur
    const fresh: Record<string, string> = {}
    out.set(id, fresh)
    return fresh
  }

  // ---------- 1) 进度列（依赖 status 列的值，无跨条目依赖） ----------
  const batteries = columns.filter((c) => c.type === 'columns-battery')
  if (batteries.length > 0) {
    for (const it of items) {
      const patch = ensure(it.id)
      for (const col of batteries) patch[col.key] = batteryValue(columns, col, it.values)
    }
  }

  // ---------- 2) 公式列（拓扑序 + 环检测） ----------
  const formulaCols: FormulaCol[] = columns
    .filter((c) => c.type === 'formula')
    .map((c) => {
      const parsed = parseFormula(readFormulaSetting(c))
      const deps: string[] = []
      for (const ref of formulaReferences(parsed)) {
        const target =
          columns.find((x) => x.title === ref) ?? columns.find((x) => x.key === ref)
        if (target !== undefined && target.type === 'formula') {	// 自引用也算依赖（否则 {自己} 不进环检测）
          deps.push(target.key)
        }
      }
      return { col: c, parsed, deps }
    })
  if (formulaCols.length === 0) return out

  const byKey = new Map(formulaCols.map((f) => [f.col.key, f]))
  const state = new Map<string, 0 | 1 | 2>()
  const cyclic = new Set<string>()
  const order: string[] = []

  const dfs = (key: string, stack: string[]): void => {
    const st = state.get(key) ?? 0
    if (st === 2) return
    if (st === 1) {
      const at = stack.indexOf(key)
      if (at >= 0) for (const s of stack.slice(at)) cyclic.add(s)
      return
    }
    state.set(key, 1)
    stack.push(key)
    const node = byKey.get(key)
    if (node !== undefined) for (const dep of node.deps) dfs(dep, stack)
    stack.pop()
    state.set(key, 2)
    order.push(key)
  }
  for (const f of formulaCols) dfs(f.col.key, [])

  // 逐列求值：同列内对每个条目求值（引用其它公式列时，那列已经在 values 里）
  for (const key of order) {
    const node = byKey.get(key)
    if (node === undefined) continue
    for (const it of items) {
      const patch = ensure(it.id)
      if (cyclic.has(key)) {
        patch[key] = '#CYCLE!'
        continue
      }
      const values = { ...it.values, ...patch }
      patch[key] = evaluateFormula(node.parsed, {
        columns,
        items,
        values,
        // 聚合要看到"已经算好的派生值"，否则合计类公式不会跟随更新
        resolve: (target, colKey) => out.get(target.id)?.[colKey] ?? target.values[colKey] ?? '',
        patchFor: (itemId) => out.get(itemId),
      })
    }
  }

  return out
}
